import { ComponentBase } from 'app/shared/lib/essette-site';
import { autoinject, bindingMode, PLATFORM } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { Router, RouterConfiguration, NavModel } from 'aurelia-router';
import { guid } from 'app/shared/lib/guid';
import { IAppRouteConfig } from 'app/shared/config/router-types';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { MemberDisplayChangeEvent } from 'app/cd/lib/events';
import * as CD from "app/cd/models/essette-api";

@autoinject()
export class MemberPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public member: CD.Member | null = null;
    protected memberId!: string;
    protected memberNavTabIds: string[] = [];
    protected hasMergeHistory: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected showMemberSummaryLink: boolean = false;

    protected memberDisplayReload: Subscription | null = null;

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly memberService: MemberService,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;

        this.checkPermissions();
    }

    protected async activate({ memberId }: { memberId: string }): Promise<void | false> {
        this.memberId = memberId;
        await this.loadMember(memberId);
        await this.loadMemberMergeResults();

        // listen for updates
        this.memberDisplayReload = this.events.subscribe(MemberDisplayChangeEvent, async (e: MemberDisplayChangeEvent) => {
            this.loadMember(memberId);
        });
    }

    protected deactivate(): void {
        if(this.memberDisplayReload) this.memberDisplayReload?.dispose();
    }

    ////****************** Functions ************************************************////

    protected async configureRouter(config: RouterConfiguration, router: Router): Promise<void> {
        await this.accountContext.load();
        const routes: IAppRouteConfig[] = [
            { route: '', redirect: 'info' },
            // Tab Navigation
            {
                route: 'info', name: 'core.admin.member.info', moduleId: PLATFORM.moduleName('app/core/pages/admin/member/member-info-page'), title: 'core.admin.member.info', nav: true,
                settings: { isVisible: true, topic: 'core.admin.member.info' }
            },
            {
                route: 'eligibility', name: 'core.admin.member.eligibility', moduleId: PLATFORM.moduleName('app/core/pages/admin/member/member-eligibility-page'), title: 'core.admin.member.eligibility', nav: true,
                settings: { isVisible: true, topic: 'core.admin.member.eligibility' }
            },
            {
                route: 'providers', name: 'core.admin.member.providers', moduleId: PLATFORM.moduleName('app/core/pages/admin/member/member-providers-page'), title: 'core.admin.member.providers', nav: true,
                settings: { isVisible: true, topic: 'core.admin.member.providers' }
            },
            {
                route: 'codes', name: 'core.admin.member.codes', moduleId: PLATFORM.moduleName('app/core/pages/admin/member/member-codes-page'), title: 'core.admin.member.codes', nav: true,
                settings: { isVisible: true, topic: 'core.admin.member.codes' }
            },
            {
                route: 'contacts', name: 'core.admin.member.contacts', moduleId: PLATFORM.moduleName('app/core/pages/admin/member/member-contacts-page'), title: 'core.admin.member.contacts', nav: true,
                settings: { isVisible: true, topic: 'core.admin.member.contacts' }
            },
            {
                route: 'merge-history', name: 'core.admin.member.merge-history', moduleId: PLATFORM.moduleName('app/core/pages/admin/member/member-merge-history-page'), title: 'core.admin.member.merge-history', nav: true,
                activationStrategy: 'invoke-lifecycle', settings: { isVisible: true, topic: 'core.admin.member.merge-history' }
            }
        ];
        config.map(routes);

        config.mapUnknownRoutes('app/shared/components/not-found');

        this.router = router;
    }

    protected memberNavigationTabs(): Array<NavModel> {
        // need to determine whether or not to show Merge History tab based on existence of Merge History data for this member
        const tabs = this.router.navigation.filter(tab => tab.config.settings.isVisible && (tab.config.route != 'merge-history' || this.hasMergeHistory));
        this.memberNavTabIds.splice(0);
        tabs.forEach((tab) => { this.memberNavTabIds.push(guid()); });
        return tabs;
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMember(memberId: string): Promise<void> {
        const includes: string[] = [];
        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMember(memberId, includes);
            this.member = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async loadMemberMergeResults(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: 1,
            includeInactive: false
        };

        const fields: string[] = ['id'];

        try {
            this.state.beginLoad();
            const response = await this.memberService.loadMemberMergeResults(this.memberId, options, undefined, fields);
            this.hasMergeHistory = response.results.length > 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
